/**
 * paymentsApi.js
 *
 * Talks to the Stripe Connect onboarding Lambdas so a landlord/PM can set up
 * a Stripe account to receive rent payments.
 *
 * Auth: All calls go through apiFetch, which sends the HttpOnly
 * firebaseToken cookie set by the /login Lambda and transparently refreshes
 * it (or signs the user out) if the cookie has gone stale.
 */
import { apiFetch } from "./apiFetch";

const API_BASE_URL =
  import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

/**
 * Creates (or reuses) the caller's Stripe connected account and returns a
 * fresh Account Session client secret for Stripe Connect embedded
 * onboarding. Pass `clientSecret` to `loadConnectAndInitialize` — do NOT
 * redirect the browser anywhere; onboarding renders inline in Porcher.
 * @returns {Promise<{ accountId: string, clientSecret: string }>}
 */
export async function createStripeOnboarding() {
  const res = await apiFetch(`${API_BASE_URL}/createStripeOnboarding`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `createStripeOnboarding returned ${res.status}`);
  }
  return res.json();
}

/**
 * Checks whether the caller has a Stripe account connected and whether
 * onboarding is fully complete. Safe to call on every Payments page load —
 * it also refreshes the stored status in Firestore.
 * @returns {Promise<{ connected: boolean, onboardingComplete: boolean, accountId: string|null, requirementsDue?: string[] }>}
 */
export async function getStripeAccountStatus() {
  const res = await apiFetch(`${API_BASE_URL}/getStripeAccountStatus`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getStripeAccountStatus returned ${res.status}`);
  }
  return res.json();
}

/**
 * Requests a fresh, single-use Stripe Express Dashboard login link for the
 * caller's connected account. Never store/reuse the returned URL — mint a
 * new one on every "View in Stripe" click.
 * @returns {Promise<{ url: string }>}
 */
export async function createStripeDashboardLink() {
  const res = await apiFetch(`${API_BASE_URL}/createStripeDashboardLink`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `createStripeDashboardLink returned ${res.status}`);
  }
  return res.json();
}

/**
 * Tenant-facing: creates a Stripe PaymentIntent for one month's rent on a
 * lease/rent-setup, destined for the landlord's connected account. Bundles
 * one or more line items (`{ type: "rent", month: "2026-01" }` or
 * `{ type: "deposit" }`) into a single PaymentIntent, so paying e.g. a
 * security deposit + first month + last month at move-in only incurs one
 * Stripe transaction fee instead of one per item. Dollar amounts are never
 * sent by the client — the Lambda looks each item's amount up from the
 * lease itself. The tenant must pick `paymentMethod` ("ach" | "card") up
 * front — the Lambda uses it to add the matching service fee on top of the
 * combined subtotal and to restrict the PaymentIntent to that single Stripe
 * payment_method_type (so Stripe Elements can't let the tenant switch
 * methods after the fee is quoted).
 * Returns a client secret for Stripe Elements — the tenant's browser never
 * talks to Stripe's charge APIs with anything other than this secret. The
 * actual `payments` Firestore record is written server-side by the
 * stripeWebhook Lambda once the payment succeeds, not by the client.
 * @param {{ leaseId: string, items: Array<{ type: "rent", month: string } | { type: "deposit" }>, paymentMethod: "ach" | "card" }} params
 * @returns {Promise<{ clientSecret: string, paymentIntentId: string, amount: number, feeAmount: number, subtotalAmount: number }>}
 */
export async function createRentPaymentIntent({ leaseId, items, paymentMethod }) {
  const res = await apiFetch(`${API_BASE_URL}/createRentPaymentIntent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leaseId, items, paymentMethod }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `createRentPaymentIntent returned ${res.status}`);
  }
  return res.json();
}

/**
 * Applicant-facing: creates a Stripe PaymentIntent for the one-time rental
 * application fee, tied to a not-yet-submitted draft (pendingApplications
 * doc). The application itself is only created once this payment succeeds
 * — see stripeWebhook.py's handle_application_fee_succeeded. Unlike rent,
 * this charge stays on Porcher's own Stripe account (no connected-account
 * transfer) — it's Porcher's fee, not the landlord's.
 * @returns {Promise<{ clientSecret: string, paymentIntentId: string, amount: number }>}
 */
export async function createApplicationFeePaymentIntent({ pendingApplicationId }) {
  const res = await apiFetch(`${API_BASE_URL}/createApplicationFeePaymentIntent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pendingApplicationId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `createApplicationFeePaymentIntent returned ${res.status}`);
  }
  return res.json();
}

/**
 * Tenant/PM-facing: every payment recorded on a lease, regardless of who
 * triggered it — lets co-tenants on the same lease see each other's payment
 * history/deposit status. Server-side (Admin SDK), not a client Firestore
 * query: the equivalent `payments` rules-based read kept returning
 * "Missing or insufficient permissions" live even with correctly-fielded
 * documents and confirmed-deployed rules (same dead end already hit for
 * rental application co-applicants — see getApplicantsForApplication.py).
 * @returns {Promise<{ payments: Array<object> }>}
 */
export async function getLeasePayments(leaseId) {
  const res = await apiFetch(`${API_BASE_URL}/getLeasePayments?leaseId=${encodeURIComponent(leaseId)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getLeasePayments returned ${res.status}`);
  }
  return res.json();
}
